/**
 * Displays a list of uploaded consent files with actions.
 * Supports viewing and deleting individual files.
 *
 * @component
 * @param {Array} files - List of file objects to display.
 * @param {Function} onDelete - Callback when a file is deleted.
 * @param {Function} onView - Callback when a file is viewed.
 */

import React, { useState, useContext } from 'react';
import { List, Avatar, Tag, Dropdown, Space, message, Popconfirm } from 'antd';
import { FileImageOutlined, FilePdfOutlined, MoreOutlined } from '@ant-design/icons';
import './consent-file-list.scss';
import { Button, GlobalContext } from 'soxo-bootstrap-core';
import { Bills } from '../../models';

export const actions = {
    VIEW: 'view',
    DELETE: 'delete',
};

const ConsentFilesList = ({ files, onDelete, onView, selectedDocCode, referenceId, onPremiseUrl, loadUploadedFiles }) => {
    const { settings } = useContext(GlobalContext);

    const { showPublishReport: isShowPublishReportBtn, type } = settings?.RPTPUB || {}; // Enable publish report button

    const [publishingFileId, setPublishingFileId] = useState(null); // Track which file is currently being published

    // Handle menu actions (view or delete)
    const handleMenuClick = (key, fileName) => {
        if (key === actions.VIEW) {
            onView(fileName);
        } else if (key === actions.DELETE) {
            onDelete(fileName);
            message.success(`${fileName.name} deleted`);
        }
    };

    // Dynamic menu items
    const menuItems = (file) => {
        const items = [
            {
                key: actions.VIEW,
                label: 'View',
                onClick: () => handleMenuClick(actions.VIEW, file),
            },
        ];

        // Only show "Delete" if not uploaded
        if (file.status === 'Pending' || file.status === 'Uploaded') {
            items.push({
                key: actions.DELETE,
                label: (
                    <Popconfirm
                        title="Are you sure you want to delete the file?"
                        onConfirm={() => handleMenuClick(actions.DELETE, file)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <span>Delete</span>
                    </Popconfirm>
                ),
            });
        }

        return items;
    };

    /**
     * To publish report
     */

    async function publishDocument(file) {
        setPublishingFileId(file?.docdet_id); // mark only this file as loading

        //publish report
        let formbody = {
            reference_number: referenceId,
            doc_code: selectedDocCode,
            mod: 'DOC',
            type: type,
            doc_id: parseInt(file?.docdet_id),
        };

        //Upload report to aws
        const res = await Bills.publishReport(onPremiseUrl, formbody);

        setPublishingFileId(null);

        if (res?.success === true) {
            message.success('Report Published Successfully');
            await loadUploadedFiles(selectedDocCode, file);
        } else {
            message.error('Failed to publish report');
        }
    }

    /**
     * if enable the publish report button when isPublishable === true;
     * @param {*} options
     * @returns
     */
    // Derive isPublishable cleanly
    const publishDocumentButtonEnabled = (file) => {
        if (!file) return false;

        if (file.type === 'png') {
            return false;
        }

        const details = file.otherDetails || {};
        const isPublishable = details?.isPublishable === true;
        const isPublished = details?.published === true;

        // Button logic:
        // - Show "Published" when published = true (disabled)
        // - Enable Publish when upload complete & isPublishable = true
        return file.status === 'Uploaded' && isPublishable && !isPublished;
    };

    return (
        <div className="files">
            <h4 className="files__title">Files</h4>

            <List
                itemLayout="horizontal"
                dataSource={files}
                renderItem={(file) => (
                    <List.Item
                        actions={[
                            <Space key="actions">
                                {isShowPublishReportBtn &&
                                    (publishDocumentButtonEnabled(file) ? (
                                        <Button
                                            size="small"
                                            type="secondary"
                                            loading={publishingFileId === file?.docdet_id} // show loader only for this file
                                            onClick={() => publishDocument(file)} // pass current file
                                        >
                                            Publish
                                        </Button>
                                    ) : (
                                        <Button size="small" type="secondary" disabled>
                                            {file.otherDetails?.published ? 'Published' : 'Publish'}
                                        </Button>
                                    ))}
                                <Tag
                                    color={file.status === 'Uploaded' ? 'green' : 'orange'}
                                    className={`consent-upload-status ${file.status === 'Pending' ? 'pending' : 'uploaded'}`}
                                >
                                    {file.status}
                                </Tag>
                                <Dropdown menu={{ items: menuItems(file) }} trigger={['click']} placement="bottomRight">
                                    <MoreOutlined className="file-action-icon" />
                                </Dropdown>
                            </Space>,
                        ]}
                    >
                        <List.Item.Meta
                            avatar={
                                <Avatar
                                    className="file-action-icon-avatar"
                                    icon={/\.(png|jpg|jpeg|gif|bmp|webp)$/i.test(file.name) ? <FileImageOutlined /> : <FilePdfOutlined />}
                                />
                            }
                            title={<span>{file.name}</span>}
                        />
                    </List.Item>
                )}
            />
        </div>
    );
};

export default ConsentFilesList;
